import React, { FunctionComponent, useEffect, useRef } from 'react'
import { ReactComponent as HealthCheckLight } from '../../assets/img/brief-thumb-health-check.svg'
import { ReactComponent as HealthCheckDark } from '../../assets/img/brief-thumb-health-check-dark.svg'
import { ReactComponent as FleetMakeupLight } from '../../assets/img/brief-thumb-fleet-makeup.svg'
import { ReactComponent as FleetMakeupDark } from '../../assets/img/brief-thumb-fleet-makeup-dark.svg'
import { ReactComponent as SecurityLight } from '../../assets/img/brief-thumb-security.svg'
import { ReactComponent as SecurityDark } from '../../assets/img/brief-thumb-security-dark.svg'
import { prefersReducedMotion } from './countUp'
import styles from './BriefThumbWipe.module.css'

/*
 * A brief's thumbnail drawn inline, so it can be taken apart and put back together the way the Overview's Briefs
 * illustration builds itself: the sidebar slides up, its sparkles pop in and its lines wipe in, the cards drop in
 * one by one with their contents (lines wiping in, dots popping, bars growing, donuts drawing round), and the notes
 * slide in last. While the brief's data is being wiped, that plays in reverse, taking the page apart down to the
 * bare background; once the wipe is done, it plays forwards, as the brief will be rebuilt. Both themes' artwork is
 * in the page (CSS shows the one that matches) and both are animated, so switching theme mid-wipe is seamless.
 */

const ART = {
  'health-check': [HealthCheckLight, HealthCheckDark],
  'fleet-makeup': [FleetMakeupLight, FleetMakeupDark],
  security: [SecurityLight, SecurityDark],
}

export type WipeableBrief = keyof typeof ART

type Kind = 'sidebar' | 'card' | 'note' | 'line' | 'bar' | 'segment' | 'pop'

const SLIDE = 'cubic-bezier(0.22, 1, 0.36, 1)'
const SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)'

/* how each kind of piece comes in (from this, to as drawn), how long it takes, and about what */
const ENTRANCES: Record<Kind, { from: Keyframe; ms: number; easing: string; origin: string }> = {
  sidebar: { from: { opacity: 0, transform: 'translateY(24px)' }, ms: 700, easing: SLIDE, origin: '50% 50%' },
  card: { from: { opacity: 0, transform: 'translateY(-16px) scale(0.9)' }, ms: 600, easing: SPRING, origin: '50% 50%' },
  note: { from: { opacity: 0, transform: 'translateX(-14px)' }, ms: 700, easing: SLIDE, origin: '50% 50%' },
  line: { from: { transform: 'scaleX(0)' }, ms: 500, easing: SLIDE, origin: '0 50%' },
  bar: { from: { transform: 'scaleY(0)' }, ms: 600, easing: SPRING, origin: '50% 100%' },
  segment: { from: {}, ms: 350, easing: 'linear', origin: '50% 50%' },
  pop: { from: { opacity: 0, transform: 'scale(0)' }, ms: 450, easing: SPRING, origin: '50% 50%' },
}

interface Piece {
  el: SVGGraphicsElement
  kind: Kind
  box: DOMRect
  /* when it comes in, in seconds from the start of the build */
  start: number
}

const centre = (box: DOMRect) => [box.x + box.width / 2, box.y + box.height / 2]
const holds = (outer: DOMRect, inner: DOMRect) => {
  const [x, y] = centre(inner)
  return x >= outer.x && x <= outer.x + outer.width && y >= outer.y && y <= outer.y + outer.height
}
const byPosition = (a: Piece, b: Piece) => Math.round(a.box.y / 8) - Math.round(b.box.y / 8) || a.box.x - b.box.x

/* the artwork's pieces (all but its background, the first), each sorted into a kind by its shape and position, and
   given its place in the build: containers first, then what's in them */
const plan = (svg: SVGSVGElement): Piece[] => {
  const { x: left, width, height } = svg.viewBox.baseVal
  const all: Piece[] = Array.from(svg.children)
    .slice(1)
    .filter((el): el is SVGGraphicsElement => typeof (el as SVGGraphicsElement).getBBox === 'function')
    .map(el => {
      const box = el.getBBox()
      const { width: w, height: h } = box
      let kind: Kind = 'pop'
      if (el.tagName === 'circle' && el.getAttribute('stroke-dasharray')) kind = 'segment'
      else if (box.x - left > width * 0.76) kind = 'note'
      else if (h <= 8 && w >= h * 2) kind = 'line'
      else if (w <= 12 && h > w * 1.5) kind = 'bar'
      else if (h > height * 0.5 && box.x - left < width * 0.2) kind = 'sidebar'
      else if (w > 30 || h > 30) kind = 'card'
      return { el, kind, box, start: 0 }
    })

  const containers = all.filter(p => p.kind === 'sidebar' || p.kind === 'card')
  const parentOf = (piece: Piece) =>
    containers
      .filter(
        c => c !== piece && c.box.width * c.box.height > piece.box.width * piece.box.height && holds(c.box, piece.box)
      )
      .sort((a, b) => a.box.width * a.box.height - b.box.width * b.box.height)[0]

  // the sidebar, then the cards that sit on the page, one by one, then any cards inside them
  const roots = containers
    .filter(c => !parentOf(c))
    .sort((a, b) => (a.kind === 'sidebar' ? -1 : 0) - (b.kind === 'sidebar' ? -1 : 0) || byPosition(a, b))
  let next = 0
  roots.forEach(c => {
    c.start = c.kind === 'sidebar' ? 0 : 0.3 + next * 0.12
    if (c.kind !== 'sidebar') next += 1
  })
  containers
    .filter(c => parentOf(c))
    .forEach(c => {
      c.start = (parentOf(c)?.start ?? 0) + 0.2
    })
  const lastCard = Math.max(0, ...containers.map(c => c.start))

  // what's in each container comes in once it has landed: lines, dots and sparkles, bars, donuts drawing round
  const counts = new Map<Piece | undefined, number>()
  const segmentEnds = new Map<string, number>()
  all
    .filter(p => !containers.includes(p) && p.kind !== 'note')
    .sort(byPosition)
    .forEach(p => {
      const parent = parentOf(p)
      const j = counts.get(parent) ?? 0
      counts.set(parent, j + 1)
      const base = parent ? parent.start + 0.25 : 0.4
      if (p.kind === 'segment') {
        // a donut's segments draw round one after another
        const key = `${Math.round(p.box.x)}:${Math.round(p.box.y)}`
        const at = segmentEnds.get(key) ?? base + 0.05
        p.start = at
        segmentEnds.set(key, at + ENTRANCES.segment.ms / 1000)
      } else if (p.kind === 'bar') {
        p.start = (parent ? base + 0.05 : lastCard + 0.2) + j * 0.04
      } else {
        p.start = base + Math.min(j * 0.05, 0.5)
      }
    })

  // the notes slide in last
  all
    .filter(p => p.kind === 'note')
    .sort(byPosition)
    .forEach((p, i) => {
      p.start = lastCard + 0.5 + Math.floor(i / 2) * 0.15
    })
  return all
}

/* a piece's entrance, as keyframes from its starting state to as drawn */
const entrance = (piece: Piece): Keyframe[] => {
  if (piece.kind === 'segment') {
    const [segment = '0', circumference = '0'] = (piece.el.getAttribute('stroke-dasharray') ?? '').split(/[\s,]+/)
    return [{ strokeDasharray: `0 ${circumference}` }, { strokeDasharray: `${segment} ${circumference}` }]
  }
  const { from } = ENTRANCES[piece.kind]
  return [from, { opacity: 1, transform: 'none' }]
}

/* the build, played forwards (rebuilding) or in reverse (taking it apart), `pace` times as long as the original */
const play = (svg: SVGSVGElement, reverse: boolean, pace: number): Animation[] => {
  const pieces = plan(svg)
  const total = Math.max(0, ...pieces.map(p => p.start + ENTRANCES[p.kind].ms / 1000))
  return pieces.map(piece => {
    const { ms, easing, origin } = ENTRANCES[piece.kind]
    // each piece moves about its own point; not the donuts' segments, which are turned by their own transform (that
    // a transform-origin would move) and only draw round
    if (piece.kind !== 'segment') {
      // eslint-disable-next-line no-param-reassign -- the piece's own point
      piece.el.style.transformOrigin = origin
    }
    const end = piece.start + ms / 1000
    return piece.el.animate(entrance(piece), {
      duration: ms * pace,
      delay: (reverse ? total - end : piece.start) * 1000 * pace,
      easing,
      direction: reverse ? 'reverse' : 'normal',
      fill: reverse ? 'forwards' : 'backwards',
    })
  })
}

interface Props {
  brief: WipeableBrief
  /** the brief's data is being wiped */
  wiping: boolean
  className?: string
}

export const BriefThumbWipe: FunctionComponent<Props> = ({ brief, wiping, className = '' }) => {
  const root = useRef<HTMLDivElement>(null)
  const wasWiping = useRef(false)
  /* the taking apart under way, which the rebuild waits for (a quick request shouldn't cut it short) */
  const takenApart = useRef<Promise<unknown>>(Promise.resolve())
  const held = useRef<Animation[]>([])

  useEffect(() => {
    const started = wiping && !wasWiping.current
    const finished = !wiping && wasWiping.current
    wasWiping.current = wiping
    const el = root.current
    if (!el || typeof el.animate !== 'function' || prefersReducedMotion()) return
    const svgs = Array.from(el.querySelectorAll('svg'))

    if (started) {
      held.current.forEach(animation => animation.cancel())
      held.current = svgs.flatMap(svg => play(svg, true, 0.55))
      takenApart.current = Promise.all(held.current.map(a => a.finished.catch(() => undefined)))
    }

    if (finished) {
      takenApart.current.then(() => {
        held.current.forEach(animation => animation.cancel())
        held.current = []
        svgs.forEach(svg => play(svg, false, 0.8))
      })
    }
  }, [wiping])

  const [Light, Dark] = ART[brief]
  return (
    <div ref={root} className={`${styles.thumb} ${className}`} aria-hidden='true'>
      <Light className={styles.lightOnly} />
      <Dark className={styles.darkOnly} />
    </div>
  )
}
